import { useState } from 'react';
import { Gauge, X, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ConflictGauge({ relationshipId, charAName, charBName, isPro, onClose }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const analyzeConflict = async () => {
    if (!isPro) {
      toast.error('Conflict Gauge is a Pro feature');
      return;
    }

    setLoading(true);
    try {
      const response = await authFetch(`${API}/relationships/conflict-gauge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ relationship_id: relationshipId }),
      });

      if (response.status === 403) {
        toast.error('Upgrade to Pro for Conflict Gauge');
        return;
      }

      if (!response.ok) throw new Error('Analysis failed');

      const data = await response.json();
      setResult(data);
    } catch (error) {
      console.error('Conflict gauge error:', error);
      toast.error('Failed to analyze conflict');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score <= 3) return 'text-yellow-500';
    if (score <= 6) return 'text-orange-500';
    return 'text-red-500';
  };

  const getScoreLabel = (score) => {
    if (score <= 3) return 'Low Tension';
    if (score <= 6) return 'Moderate Conflict';
    if (score <= 8) return 'High Stakes';
    return 'Explosive';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={onClose}>
      <div className="bg-card border border-primary/30 p-8 max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Gauge className="w-6 h-6 text-primary" />
            <h3 className="font-mono text-xl font-semibold">Conflict Gauge</h3>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {!result && !loading && (
          <>
            <div className="text-center mb-6">
              <p className="text-muted-foreground mb-2">
                Analyze the conflict between:
              </p>
              <p className="font-mono text-lg">
                <span className="text-primary">{charAName}</span>
                {' '}↔{' '}
                <span className="text-primary">{charBName}</span>
              </p>
            </div>

            <p className="text-sm text-muted-foreground mb-6 text-center">
              The Conflict Gauge will analyze moral differences, tactical opposition, 
              and central arguments to provide a conflict score and tips for heightening stakes.
            </p>

            <Button
              onClick={analyzeConflict}
              disabled={loading || !isPro}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3"
            >
              <Gauge className="w-4 h-4 mr-2" />
              Analyze Conflict
            </Button>
          </>
        )}

        {loading && (
          <div className="text-center py-8">
            <Loader2 className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" />
            <p className="text-muted-foreground">Analyzing conflict dynamics...</p>
          </div>
        )}

        {result && (
          <>
            {/* Score Display */}
            <div className="text-center mb-8">
              <div className={`text-6xl font-mono font-bold ${getScoreColor(result.score)}`}>
                {result.score}/10
              </div>
              <p className={`font-mono text-sm uppercase tracking-wider mt-2 ${getScoreColor(result.score)}`}>
                {getScoreLabel(result.score)}
              </p>
            </div>

            {/* Visual Gauge */}
            <div className="h-4 bg-secondary rounded-none overflow-hidden mb-8">
              <div 
                className={`h-full transition-all ${
                  result.score <= 3 ? 'bg-yellow-500' :
                  result.score <= 6 ? 'bg-orange-500' : 'bg-red-500'
                }`}
                style={{ width: `${result.score * 10}%` }}
              />
            </div>

            {/* Tips */}
            <div className="space-y-3">
              <h4 className="font-mono text-sm uppercase text-primary flex items-center gap-2">
                {result.score < 7 ? (
                  <AlertTriangle className="w-4 h-4" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                Stakes-Heightening Tips
              </h4>
              <ul className="space-y-2">
                {result.tips.map((tip, i) => (
                  <li 
                    key={i} 
                    className="text-sm text-muted-foreground bg-background/50 p-3 border-l-2 border-primary/50"
                  >
                    {tip}
                  </li>
                ))}
              </ul>
            </div>

            <Button
              onClick={onClose}
              variant="outline"
              className="w-full rounded-none font-mono uppercase tracking-widest text-xs mt-6"
            >
              Close
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
